import axios from 'axios';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import Collapsible from 'react-collapsible';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  NodeServer,
  Race,
  TeamCollectionName,
  avatarBackgroundArr,
  avatarRaceToClotheMap,
  avatarRaceToImgMap,
  getRandomNumber,
} from '../config';
import aboutBoxImg from '../images/about-box.png';
import avatarClose from '../images/avatar-close.png';
import dividerImg from '../images/divider-line.png';
import softServeLogo from '../images/soft-serve-logo.png';
import '../styles/Avatar.scss';
import Loading from './Loading';

const newAvatarId = 1292;

function Avatar(props: { title: string }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [avatar, setAvatar] = useState({
    id: 0,
    nftId: 0,
    image: '',
    name: '',
    description: '',
    nickNameMeaning: '',
    occupationDescription: '',
    occupationName: '',
    prediction: '',
    openseaUrl: '',
    createdDate: new Date(),
    collection: '',
    raceName: '',
    worldName: '',
  });

  const [avatarBackground, setAvatarBackground] = useState({
    background: '',
    backgroundBlendMode: '',
  });

  const [newAvatarBackground, setNewAvatarBackground] = useState({
    backgroundImage: '',
    backgroundSize: '',
    backgroundColor: '',
  });

  const onCloseClick = () => {
    navigate(-1);
  };

  const navigateToOpenSea = () => {
    window.open(avatar.openseaUrl);
  };

  useEffect(() => {
    const getNftDetails = async () => {
      const nftId = searchParams.get('id');
      const response = await axios.get<any>(
        `${NodeServer.baseURL}${NodeServer.nftDetails}${nftId}`,
        { withCredentials: true }
      );
      setAvatar({
        ...response.data,
        createdDate: new Date(response.data.createdOn),
      });
      setLoading(false);
      if (response.data.collection !== TeamCollectionName) {
        if (response.data.raceName) {
          setNewAvatarBackground({
            backgroundImage: `url(${
              avatar.id < newAvatarId
                ? avatarRaceToImgMap[response.data.raceName as Race]
                : ''
            })`,
            backgroundSize: 'cover',
            backgroundColor: 'black',
          });
        } else {
          const index = getRandomNumber(10);
          setAvatarBackground(avatarBackgroundArr[index]);
        }
      } else {
        setAvatarBackground({
          background: 'black',
          backgroundBlendMode: '',
        });
      }
    };
    getNftDetails();
  }, [searchParams, avatar.image, avatar.id]);

  useEffect(() => {
    document.title = props.title;
  });

  return loading ? (
    <div className="loading-container">
      <Loading />
    </div>
  ) : (
    <div
      className="avatar-page"
      style={
        avatar.raceName
          ? {
              ...newAvatarBackground,
            }
          : {
              ...avatarBackground,
            }
      }
    >
      <div className="avatar-top">
        <div className="avatar-description">
          <div className="logos">
            <img
              className="soft-serve-logo"
              src={softServeLogo}
              alt="logo"
            ></img>
          </div>
          <div
            className="small-nft"
            style={
              avatar.raceName
                ? {
                    ...newAvatarBackground,
                  }
                : {
                    ...avatarBackground,
                  }
            }
          >
            <img className="nft-img" alt="avatar" src={avatar.image}></img>
            {!!avatar.raceName && avatar.id < newAvatarId && (
              <>
                <img
                  className="avatar-badge"
                  alt="avatar"
                  src={avatarRaceToClotheMap[avatar.raceName as Race]}
                ></img>
              </>
            )}
          </div>
          <img className="divider" src={dividerImg} alt="divider"></img>
          <div className="avatar-properties">
            {!!avatar.worldName && avatar.id < newAvatarId && (
              <div>
                <span className="avatar-world">WORLD: {avatar.worldName}</span>
              </div>
            )}
            {!!avatar.raceName && avatar.id < newAvatarId && (
              <div>
                <span className="avatar-race">RACE: {avatar.raceName}</span>
              </div>
            )}
          </div>
          <div className="avatar-title">
            <h1 className="avatar-name">{avatar.name}</h1>
            <p
              className="avatar-nick-name"
              dangerouslySetInnerHTML={{ __html: avatar.nickNameMeaning }}
            ></p>
            <h3 className="avatar-occupation">{avatar.occupationName}</h3>
            <p className="avatar-occupation-description">
              {avatar.occupationDescription}
            </p>
          </div>

          {avatar.prediction && (
            <div className="avatar-about">
              <p className="about-title">PREDICTION</p>
              <p className="about-text">{avatar.prediction}</p>
            </div>
          )}

          <button className="marketplace-btn" onClick={navigateToOpenSea}>
            View on OpenSea
          </button>

          <p className="details-title">NFT DETAILS</p>
          <div className="avatar-details">
            <div className="titles">
              <p>
                Date: {format(avatar.createdDate, 'MMMM dd, yyyy, hh:mm aaa')}
              </p>
              <p>Event: {avatar.collection}</p>
              <p>Owner: @{avatar.name}</p>
              <p>Author: @Alnair-SoftServe</p>
            </div>
          </div>

          <div className="about-box-container">
            <img className="about-box-img" alt="about" src={aboutBoxImg}></img>
            <p className="about-text">
              Wandering the booths of the event, you end up in unique,
              mysterious place. That’s the moment you meet Alnair – who told you
              your future.
            </p>
          </div>

          <div className="avatar-about">
            <p className="about-text">
              This automatically-generated 3D avatar solution has been designed
              with the enthusiasm of immersive experience creators at SoftServe,
              Ukraine.
            </p>
          </div>

          <div className="avatar-faq">
            <p className="details-title">FAQ</p>
            <Collapsible trigger="What is it?">
              <p>
                This is your NFT. This graphical asset represents your avatar,
                which SoftServe created using synthetic human technology. It is
                accompanied by a prediction of your future occupation in the
                digital world.
              </p>
            </Collapsible>

            <Collapsible trigger="What can I do with it?">
              <p>
                Your NFT is generated in a single instance. It is unique. You
                own it and can trade it on a secondary NFT market if you want
                to. If you are interested in experiencing the demo again, and
                creating more NFTs like this one, please{' '}
                <NavLink to="/contact-us">contact SoftServe</NavLink>
                {'.'}
              </p>
            </Collapsible>

            <Collapsible trigger="How can I get my NFT?">
              <p>
                To obtain your NFT, you need to have a crypto wallet. Please
                send us your crypto wallet address via the contact form above
                or, if you don't have your own crypto wallet yet, follow the
                link on how to create it.
              </p>
            </Collapsible>
          </div>
        </div>
        {avatar.id < newAvatarId ? (
          <div className="avatar-image">
            <img alt="avatar" src={avatar.image}></img>
            {!!avatar.raceName && (
              <>
                <img
                  className="avatar-badge"
                  alt="avatar"
                  src={avatarRaceToClotheMap[avatar.raceName as Race]}
                ></img>
              </>
            )}
          </div>
        ) : (
          <div
            className="new-avatar-image"
            style={{
              backgroundImage: `url(${avatar.image})`,
            }}
          ></div>
        )}
      </div>
      <div className="avatar-bottom">
        <div className="avatar-close">
          <img src={avatarClose} alt="Close" onClick={onCloseClick} />
        </div>
      </div>
    </div>
  );
}

export default Avatar;
